import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/avatar';
import { 
  Heart, 
  MessageCircle, 
  Share2, 
  MoreVertical,
  Trash2,
  Flag,
  Bookmark,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Post } from '@/types/post';
import { Comment } from '@/types/comment';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { auth, db } from '@/lib/firebase';
import { doc, updateDoc, increment, collection, addDoc, serverTimestamp, onSnapshot, query, orderBy, setDoc, deleteDoc } from 'firebase/firestore';

interface PostCardProps {
  post: Post;
  currentUserId: string;
  onNavigateToProfile: (authorId: string) => void;
  onShowShare: (post: Post) => void;
  onDeletePost: (postId: string) => void;
  onReportPost: (postId: string) => void;
  onSavePost: (postId: string) => void;
}

export function PostCard({
  post,
  currentUserId,
  onNavigateToProfile,
  onShowShare,
  onDeletePost,
  onReportPost,
  onSavePost
}: PostCardProps) {
  const [liked, setLiked] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isAuthor] = useState(currentUserId === post.authorId);

  // Check if user has liked the post
  useEffect(() => {
    if (!currentUserId) return;
    
    const likeRef = doc(db, `posts/${post.id}/likes/${currentUserId}`);
    return onSnapshot(likeRef, (doc) => {
      setLiked(doc.exists());
    });
  }, [currentUserId, post.id]);

  // Load comments when expanded
  useEffect(() => {
    if (!showComments) return;

    const commentsRef = collection(db, `posts/${post.id}/comments`);
    const q = query(commentsRef, orderBy('createdAt', 'desc'));
    
    return onSnapshot(q, (snapshot) => {
      const commentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Comment[];
      setComments(commentsData);
    });
  }, [showComments, post.id]);

  const handleLike = async () => {
    if (!currentUserId) return;
    
    const likeRef = doc(db, `posts/${post.id}/likes/${currentUserId}`);
    const postRef = doc(db, 'posts', post.id);

    try {
      if (!liked) {
        // Add like
        await Promise.all([
          setDoc(likeRef, { 
            userId: currentUserId,
            createdAt: serverTimestamp()
          }),
          updateDoc(postRef, {
            likes: increment(1)
          })
        ]);
      } else {
        // Remove like
        await Promise.all([
          deleteDoc(likeRef),
          updateDoc(postRef, {
            likes: increment(-1)
          })
        ]);
      }
    } catch (error) {
      console.error('Error handling like:', error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !currentUserId) return;

    const commentsRef = collection(db, `posts/${post.id}/comments`);
    const postRef = doc(db, 'posts', post.id);

    try {
      await Promise.all([
        addDoc(commentsRef, {
          content: newComment,
          authorId: currentUserId,
          authorName: auth.currentUser?.displayName || 'Usuário',
          createdAt: serverTimestamp()
        }),
        updateDoc(postRef, {
          comments: increment(1)
        })
      ]);

      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp?.toDate?.() || new Date(timestamp);
    return format(date, "d 'de' MMMM", { locale: ptBR });
  };

  return (
    <Card className="overflow-hidden">
      {/* Post Header */}
      <div className="p-4 flex items-center space-x-4">
        <Avatar
          className="cursor-pointer"
          onClick={() => onNavigateToProfile(post.authorId)}
        >
          <div className="bg-blue-600 w-full h-full flex items-center justify-center text-white">
            {post.authorName?.charAt(0).toUpperCase()}
          </div>
        </Avatar>
        <div className="flex-1">
          <h3
            className="font-semibold cursor-pointer hover:text-blue-600"
            onClick={() => onNavigateToProfile(post.authorId)}
          >
            {post.authorName}
          </h3>
          <p className="text-sm text-gray-500">{post.service}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            {formatDate(post.createdAt)}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isAuthor ? (
                <DropdownMenuItem onClick={() => onDeletePost(post.id)} className="text-red-600">
                  <Trash2 className="mr-2 h-4 w-4" />
                  <span>Excluir publicação</span>
                </DropdownMenuItem>
              ) : (
                <>
                  <DropdownMenuItem onClick={() => onReportPost(post.id)}>
                    <Flag className="mr-2 h-4 w-4" />
                    <span>Denunciar publicação</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onSavePost(post.id)}>
                    <Bookmark className="mr-2 h-4 w-4" />
                    <span>Salvar publicação</span>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Post Content */}
      <div className="px-4 pb-4">
        <p className="text-gray-800 whitespace-pre-line">{post.content}</p>
      </div>

      {/* Post Images */}
      {post.images && post.images.length > 0 && (
        <div className="grid grid-cols-1 gap-2">
          {post.images.map((image, index) => (
            <img
              key={index}
              src={image}
              alt={`Post image ${index + 1}`}
              className="w-full h-auto"
            />
          ))}
        </div>
      )}

      {/* Post Actions */}
      <div className="px-4 py-3 border-t flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            className={`flex items-center gap-2 ${liked ? 'text-red-500' : ''}`}
            onClick={handleLike}
          >
            <Heart className={`h-5 w-5 ${liked ? 'fill-current' : ''}`} />
            <span>{post.likes || 0}</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-2"
            onClick={() => setShowComments(!showComments)}
          >
            <MessageCircle className="h-5 w-5" />
            <span>{post.comments || 0}</span>
            {showComments ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onShowShare(post)}
        >
          <Share2 className="h-5 w-5" />
        </Button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="px-4 py-3 border-t space-y-4">
          {/* Comment Input */}
          <div className="flex gap-2">
            <Input
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Escreva um comentário..."
              onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
            />
            <Button onClick={handleAddComment}>Enviar</Button>
          </div>

          {/* Comments List */}
          <div className="space-y-4">
            {comments.map((comment) => (
              <div key={comment.id} className="flex gap-3">
                <Avatar>
                  <div className="bg-blue-600 w-full h-full flex items-center justify-center text-white">
                    {comment.authorName?.charAt(0).toUpperCase()}
                  </div>
                </Avatar>
                <div className="flex-1">
                  <div className="bg-gray-100 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">{comment.authorName}</span>
                      <span className="text-sm text-gray-500">
                        {formatDate(comment.createdAt)}
                      </span>
                    </div>
                    <p className="text-gray-800">{comment.content}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}